import { useEffect, useState, useRef } from "react";
import { IoRefreshOutline } from "react-icons/io5";

// Without this, a deployed update sits cached until someone closes every
// open tab themselves (see serviceWorkerRegistration's onUpdate comment) —
// this surfaces it instead, and reload() only fires after the new service
// worker has actually taken control, not before.
export default function UpdateBanner() {
  const [visible, setVisible] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const registrationRef = useRef(null);

  useEffect(() => {
    function onUpdate(e) {
      registrationRef.current = e.detail;
      setVisible(true);
    }

    window.addEventListener("virsa-sw-update", onUpdate);
    return () => window.removeEventListener("virsa-sw-update", onUpdate);
  }, []);

  function refresh() {
    // A second tap before the page actually reloads (activation can take a
    // moment) would otherwise register a second "controllerchange" listener
    // and post SKIP_WAITING twice — harmless since reload() is idempotent,
    // but there's no reason not to guard it like every other action button
    // in the app, and it gives the tap visible feedback in the meantime.
    if (refreshing) return;
    setRefreshing(true);

    const registration = registrationRef.current;
    const waiting = registration?.waiting;

    if (!waiting) {
      window.location.reload();
      return;
    }

    // If activation somehow never completes, "controllerchange" never fires
    // and — now that a tap disables the button — there'd be no way back to
    // a working Refresh short of the user finding their browser's own
    // reload control themselves. This timeout guarantees a way out either
    // way; the `done` guard is just to skip a redundant second reload() on
    // the rare chance both paths fire.
    let done = false;
    function reloadOnce() {
      if (done) return;
      done = true;
      window.location.reload();
    }

    navigator.serviceWorker.addEventListener("controllerchange", reloadOnce, { once: true });
    waiting.postMessage({ type: "SKIP_WAITING" });
    setTimeout(reloadOnce, 4000);
  }

  if (!visible) return null;

  return (
    <div style={banner}>
      <span>A new version of GatherYou is available.</span>
      <button style={{ ...button, opacity: refreshing ? 0.7 : 1 }} onClick={refresh} disabled={refreshing}>
        <IoRefreshOutline style={{ verticalAlign: -2, marginRight: 4 }} />
        {refreshing ? "Refreshing…" : "Refresh"}
      </button>
    </div>
  );
}

// See OfflineBanner.jsx — deliberately not position:fixed, for the same
// reason: two fixed banners both pinned at top:0 (this and OfflineBanner)
// just draw over each other and over Navbar's sticky top bar underneath,
// instead of stacking. As a normal flow element this pushes everything
// below it down by its real height instead.
const banner = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  background: "var(--accent-primary)",
  color: "#fff",
  fontSize: 12.5,
  fontWeight: 600,
  padding: "8px 12px",
};

const button = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 12px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.5)",
  background: "rgba(255,255,255,0.15)",
  color: "#fff",
  fontWeight: 700,
  fontSize: 12,
  cursor: "pointer",
};
